import palette from "assets/theme/base/palette";
import React, { useRef, useState, useEffect, useMemo } from "react";
import { Box, Button, Card, Grid, Modal, Typography, FormLabel } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import { call } from "services/axiosService/core";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Loader from "pages/loader/Loader";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useTranslation } from "react-i18next";
import { useMaterialUIController, setProfile } from "context";
import { useStripe } from "@stripe/react-stripe-js";
import * as Sentry from "@sentry/browser";

export default function InnerPaymentRedirect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const [controller, dispatch] = useMaterialUIController();
  const { profile, persona } = controller;
  const [step, setStep] = useState(1);
  const [alertMessage, setAlertMessage] = useState("");
  const [severity, setActivity] = useState("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selfPayment, setSelfPayment] = useState(false);
  const [status, setStatus] = useState("processing");

  const { signOut } = useAuthenticator((context) => [context.user]);

  const handleSignupChild = async () => {
    await signOut();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentClientSecret = params.get("payment_intent_client_secret");
    const setupClientSecret = params.get("setup_intent_client_secret");
    const paymentIntent = params.get("payment_intent") || params.get("setup_intent");
    if ((paymentClientSecret || setupClientSecret) && stripe) {
      window.history.replaceState({}, document.title, "/payment-redirect");
      let myInterval: NodeJS.Timeout | null = null;
      let myTimeout: NodeJS.Timeout | null = null;
      let finished = false;

      const finalize = (finalStatus: string) => {
        if (finished) return; // make sure only runs once
        let paymentRedirectDetails;
        try {
          paymentRedirectDetails = localStorage.getItem("payment_redirect_details")
            ? JSON.parse(localStorage.getItem("payment_redirect_details"))
            : null;
          localStorage.removeItem("payment_redirect_details");
        } catch (error) {
          console.log(error);
        }
        paymentRedirectDetails["paymentIntentId"] = paymentIntent;
        finished = true;
        clearInterval(myInterval);
        clearTimeout(myTimeout);
        handleStatus(finalStatus, paymentRedirectDetails);
      };

      const checkStatus = async () => {
        if (finished) return;
        try {
          let intent;
          if (paymentClientSecret) {
            const result = await stripe.retrievePaymentIntent(paymentClientSecret);
            intent = result.paymentIntent;
          } else if (setupClientSecret) {
            const result = await stripe.retrieveSetupIntent(setupClientSecret);
            intent = result.setupIntent;
          }
          if (!intent) {
            return finalize("error");
          }

          switch (intent.status) {
            case "succeeded":
              return finalize("success");
            case "processing":
              setStatus("processing"); // don’t finalize yet
              break;
            case "canceled":
              return finalize("fail");
            case "failed":
              return finalize("fail");
            default:
              return finalize("error");
          }
        } catch (err) {
          return finalize("error");
        }
      };

      // run interval + timeout
      myInterval = setInterval(checkStatus, 10000);
      myTimeout = setTimeout(() => {
        if (status === "processing") {
          finalize("processing");
        } else {
          finalize(status || "error");
        }
      }, 150000);

      return () => {
        // cleanup when component unmounts
        clearInterval(myInterval);
        clearTimeout(myTimeout);
      };
    }
  }, [stripe]);

  const handleStatus = async (status, paymentRedirectDetails) => {
    try {
      const checkoutProcess = paymentRedirectDetails.checkoutProcess || null;
      if (status === "processing") {
        const response = await call(`payments/process-payment/${profile.id}`, "POST", {
          body: paymentRedirectDetails,
        });

        if (response.status === 200) {
          if (checkoutProcess === "addPayment") {
            setStep(13);
          } else {
            setStep(12);
          }
        } else {
          if (checkoutProcess === "addPayment") {
            setStep(13);
          } else {
            setStep(12); // fallback for unknown status
          }
        }
      } else {
        switch (checkoutProcess) {
          case "normal":
            updatePaymentStatus(status, paymentRedirectDetails);
            break;
          case "bulk":
            updateBulkPaymentStatus(status, paymentRedirectDetails);
            break;
          case "addPayment":
            updateAddPaymentStatus(status, paymentRedirectDetails);
            break;
          default:
            console.warn("Unknown checkout process:", checkoutProcess);
        }
      }
    } catch (error) {
      console.error("Error handling payment status:", error);
      setStep(12); // make sure UI continues gracefully
    }
  };

  const handleRedirectPage = () => {
    setStep(2);
  };

  const handleClosePaymentSuccessModal = () => {
    if (selfPayment) {
      navigate("/profile");
    } else {
      setStep(3);
    }
  };

  const gotoProfilePage = () => {
    navigate("/profile");
  };

  const updatePaymentStatus = (status, paymentRedirectDetails) => {
    const couponUsed = paymentRedirectDetails.couponUsed;
    const subscriptionType = paymentRedirectDetails.subscriptionType;
    const childDetails = paymentRedirectDetails.childDetails;
    const amountToProcess = paymentRedirectDetails.amountToProcess;

    if (status == "success") {
      if (couponUsed) {
        localStorage.removeItem("referral");
        call(`payments/apply-coupon`, "PUT", {
          body: { email: childDetails.email, code: couponUsed },
        })
          .then((response) => {
            console.log("Coupon used");
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      }
      call(`users/${profile.id}/emails`, "POST", {
        body: {
          email_type: "payment_success_email",
          recipient_id: profile.id,
          amount_paid: amountToProcess,
        },
      })
        .then((response) => {
          if (response.status == 200) {
            console.log("Email Success");
          }
        })
        .catch(function (error) {
          Sentry.captureException(error);
          console.log(error);
        });

      if (childDetails.id == profile.id) {
        setSelfPayment(true);
        let true_users = profile.true_mindsets_users ? profile.true_mindsets_users : [];
        if (subscriptionType != "maxu_yearly") {
          if (!true_users.includes(profile.id)) {
            true_users.push(profile.id);
          }
        }
        let LearnerDetails = {
          ...childDetails,
          payment_status: "success",
          paid_at: new Date(),
          subscription_type: subscriptionType,
          true_mindsets_users: true_users,
        };
        if (couponUsed) {
          const source =
            LearnerDetails.source ||
            (couponUsed && couponUsed.includes("_") ? `${couponUsed.split("_")[0]}_code` : null);
          LearnerDetails = { ...LearnerDetails, referral_code: couponUsed, source: source };
        }
        call(`users/${LearnerDetails.id}`, "PUT", {
          body: LearnerDetails,
          headers: { "Content-Type": "application/json" },
        })
          .then((response) => {
            if (response.status == 200) {
              handleRedirectPage("success");
            }
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      } else {
        let AdultDetails = profile;
        let LearnerDetails = childDetails;

        LearnerDetails = {
          ...LearnerDetails,
          payment_status: "success",
          paid_at: new Date(),
          subscription_type: subscriptionType,
        };

        if (couponUsed) {
          const source =
            LearnerDetails.source ||
            (couponUsed && couponUsed.includes("_") ? `${couponUsed.split("_")[0]}_code` : null);
          LearnerDetails = { ...LearnerDetails, referral_code: couponUsed, source: source };
        }

        let true_users = profile.true_mindsets_users ? profile.true_mindsets_users : [];
        let true_subs = ["true_mindsets_yearly", "true_mindsets_monthly"];
        if (true_subs.includes(subscriptionType)) {
          if (!true_users.includes(LearnerDetails.id)) {
            true_users.push(LearnerDetails.id);
            AdultDetails = { ...AdultDetails, true_mindsets_users: true_users };
            call(`users/${profile.id}`, "PUT", {
              body: AdultDetails,
              headers: { "Content-Type": "application/json" },
            })
              .then((response) => {
                if (response.status == 200) {
                  console.log("profile updated");
                }
              })
              .catch(function (error) {
                Sentry.captureException(error);
                console.log(error);
              });
          }
        }
        call(`users/${LearnerDetails.id}`, "PUT", {
          body: LearnerDetails,
          headers: { "Content-Type": "application/json" },
        })
          .then((response) => {
            if (response.status == 200) {
              handleRedirectPage("success");
            }
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      }
    } else {
      let LearnerDetails = childDetails;
      LearnerDetails = {
        ...LearnerDetails,
        payment_status: status,
        subscription_type: subscriptionType,
      };
      call(`users/${LearnerDetails.id}`, "PUT", {
        body: LearnerDetails,
        headers: { "Content-Type": "application/json" },
      })
        .then((response) => {
          if (response.status == 200) {
            console.log("response", response);
            setStep(11);
          }
        })
        .catch(function (error) {
          Sentry.captureException(error);
          console.log(error);
        });
    }
  };

  const updateBulkPaymentStatus = (status, paymentRedirectDetails) => {
    const subscriptionType = paymentRedirectDetails.subscriptionType;
    const learnerDetails = paymentRedirectDetails.learnerDetails;
    const amountToProcess = paymentRedirectDetails.amountToProcess;

    if (status == "success") {
      setStep(10);
      const usages = learnerDetails
        .filter((learner) => learner.code_status === "valid_code" && learner.referral_code)
        .map((learner) => ({
          referral_code: learner.referral_code,
          email: learner.email,
        }));

      const learner_ids = learnerDetails.map((learner) => learner.learner_id);

      if (usages.length > 0) {
        call(`/payments/update-bulk-coupon-usage`, "PUT", {
          body: {
            usages: usages,
          },
        })
          .then((response) => {
            if (response.status == 200) {
              console.log("update Success");
            }
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      }

      call(`users/${profile.id}/emails`, "POST", {
        body: {
          email_type: "payment_success_email",
          recipient_id: profile.id,
          amount_paid: amountToProcess,
        },
      })
        .then((response) => {
          if (response.status == 200) {
            console.log("Email Success");
          }
        })
        .catch(function (error) {
          Sentry.captureException(error);
          console.log(error);
        });

      call(`/users/bulk-upload/update-status`, "POST", {
        body: {
          learner_details: learnerDetails,
          guardian_id: profile.id,
          payment_status: "success",
          paid_at: new Date(),
          subscription_type: subscriptionType,
        },
      })
        .then((response) => {
          if (response.status == 200) {
            console.log("update Success");
            setStep(8);
          }
        })
        .catch(function (error) {
          Sentry.captureException(error);
          console.log(error);
        });

      const AdultDetails = profile;
      let true_users = profile.true_mindsets_users ? profile.true_mindsets_users : [];
      let true_subs = ["true_mindsets_yearly", "true_mindsets_monthly"];

      if (true_subs.includes(subscriptionType)) {
        true_users = Array.from(new Set([...true_users, ...learner_ids]));
        AdultDetails.true_mindsets_users = true_users;
        call(`users/${profile.id}`, "PUT", {
          body: AdultDetails,
          headers: { "Content-Type": "application/json" },
        })
          .then((response) => {
            if (response.status == 200) {
              console.log("update Success");
            }
          })
          .catch(function (error) {
            Sentry.captureException(error);
            console.log(error);
          });
      }
    } else {
      call(`/users/bulk-upload/update-status`, "POST", {
        body: {
          learner_details: learnerDetails,
          guardian_id: profile.id,
          payment_status: "fail",
          paid_at: null,
          subscription_type: subscriptionType,
        },
      })
        .then((response) => {
          if (response.status == 200) {
            console.log("update Success");
          }
        })
        .catch(function (error) {
          Sentry.captureException(error);
          console.log(error);
        });
    }
  };

  const updateAddPaymentStatus = (status, paymentRedirectDetails) => {
    if (profile.id == paymentRedirectDetails.childDetails.id) {
      setSelfPayment(true);
    }
    if (status == "success") {
      setStep(4);
    } else {
      setStep(5);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar title="Payment-Redirect" />
      <Grid item xs="10" mt={2}>
        <Dialog open>
          {step === 1 && (
            <DialogContent
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "300px",
                overflow: "hidden",
                padding: 4,
              }}
            >
              <Loader loaderText={t("loaderText.processingPayment")} />
            </DialogContent>
          )}
          {step === 2 && (
            <>
              <DialogContent>
                <Typography variant="h4" ma={1}>
                  {t("messages.subscription.paymentCompleted")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => handleClosePaymentSuccessModal()}
                  style={{
                    marginTop: "15px",
                    color: "white",
                    background: palette.brand[500],
                  }}
                >
                  {t("buttonTitles.finish")}
                </Button>
              </DialogActions>
            </>
          )}
          {step == 3 && (
            <DialogContent>
              <Typography
                variant="body1"
                fontSize={16}
                textAlign="left"
                sx={{ whiteSpace: "normal", lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{
                  __html: t("messages.subscription.thankyouMessageChild"),
                }}
              />

              <MDBox display="flex" justifyContent="space-between" mt={2}>
                <Button
                  type="button"
                  onClick={handleSignupChild}
                  style={{
                    color: "white",
                    background: palette.orange[500],
                  }}
                >
                  {t("buttonTitles.signupChild")}
                </Button>

                <Button
                  type="button"
                  onClick={gotoProfilePage}
                  style={{
                    color: "white",
                    background: palette.brand[500],
                  }}
                >
                  {t("buttonTitles.close")}
                </Button>
              </MDBox>
            </DialogContent>
          )}

          {step == 4 && (
            <>
              <DialogContent>
                <Typography variant="h4" ma={1}>
                  {t("messages.subscription.addPaymentSuccess")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => handleClosePaymentSuccessModal()}
                  style={{
                    marginTop: "15px",
                    color: "white",
                    background: palette.brand[500],
                  }}
                >
                  {t("buttonTitles.finish")}
                </Button>
              </DialogActions>
            </>
          )}

          {step == 5 && (
            <>
              <DialogContent>
                <Typography variant="h4" ma={1}>
                  {t("messages.subscription.addPaymentFail")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => gotoProfilePage()}
                  style={{
                    marginTop: "15px",
                    color: "white",
                    background: palette.brand[500],
                  }}
                >
                  {t("buttonTitles.finish")}
                </Button>
              </DialogActions>
            </>
          )}

          {step == 8 && (
            <>
              <DialogContent>
                <Typography variant="h4" ma={1}>
                  {t("messages.subscription.paymentCompleted")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setStep(9)}
                  style={{
                    marginTop: "15px",
                    color: "white",
                    background: palette.brand[500],
                  }}
                >
                  {t("buttonTitles.finish")}
                </Button>
              </DialogActions>
            </>
          )}
          {step == 9 && (
            <DialogContent>
              <Typography
                variant="body1"
                fontSize={16}
                textAlign="left"
                sx={{ whiteSpace: "normal", lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{
                  __html: t("messages.subscription.thankyouMessageChild"),
                }}
              />

              <MDBox display="flex" justifyContent="space-between" mt={2}>
                <Button
                  type="button"
                  onClick={handleSignupChild}
                  style={{
                    color: "white",
                    background: palette.orange[500],
                  }}
                >
                  {t("buttonTitles.signupChild")}
                </Button>
                <Button
                  type="button"
                  onClick={gotoProfilePage}
                  style={{
                    color: "white",
                    background: palette.brand[500],
                    marginTop: "15px",
                    display: "flex",
                    justifyContent: "end",
                  }}
                >
                  {t("buttonTitles.close")}
                </Button>
              </MDBox>
            </DialogContent>
          )}
          {step == 10 && (
            <DialogContent
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "300px",
                overflow: "hidden",
                padding: 4,
              }}
            >
              <Loader loaderText={t("loaderText.bulkAdd")} />
            </DialogContent>
          )}
          {step == 11 && (
            <>
              <DialogContent>
                <Typography variant="h4" ma={1}>
                  {t("messages.subscription.paymentFailed")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => gotoProfilePage()}
                  style={{
                    marginTop: "15px",
                    color: "white",
                    background: palette.brand[500],
                  }}
                >
                  {t("buttonTitles.finish")}
                </Button>
              </DialogActions>
            </>
          )}
          {step === 12 && (
            <>
              <DialogContent>
                <Typography variant="h4" align="center" ma={1}>
                  {t("messages.subscription.paymentProcessing")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => gotoProfilePage()}
                  style={{
                    marginTop: "15px",
                    color: "white",
                    background: palette.brand[500],
                  }}
                >
                  {t("buttonTitles.finish")}
                </Button>
              </DialogActions>
            </>
          )}
          {step === 13 && (
            <>
              <DialogContent>
                <Typography variant="h4" align="center" ma={1}>
                  {t("messages.subscription.addPaymentProcessing")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => gotoProfilePage()}
                  style={{
                    marginTop: "15px",
                    color: "white",
                    background: palette.brand[500],
                  }}
                >
                  {t("buttonTitles.finish")}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Grid>
    </DashboardLayout>
  );
}
